"use client";
import { usePathname } from "next/navigation";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase, useUser } from "../client";
import { type DerivedOrganizationState, deriveOrganizationState } from "../lib/organization-state";
import type { MembershipSummary, Organization, OrganizationSnapshot } from "../lib/organization-types";

export type { MembershipSummary, Organization, OrganizationSnapshot };

type OrganizationContextType = {
  currentOrg: Organization | null;
  organizations: Organization[];
  homeOrganizations: Organization[];
  customerOrganizations: Organization[];
  customerParentMap: Record<string, string>;
  memberships: MembershipSummary[];
  switchOrganization: (orgId: string) => Promise<void>;
  returnToAgency: (() => Promise<void>) | null;
  refreshOrganization: () => Promise<void>;
  loading: boolean;
};

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

/** Auth-related paths where we should NOT fetch organizations or sync sessions */
const AUTH_PATHS = ["/auth/callback", "/sign-in", "/sign-up", "/reset-password"];

/**
 * Writes the chosen organisation into the JWT and refreshes the session so the
 * new `app_metadata.tenant_id` is in play. Only ever called for a user who held
 * no tenant claim at all.
 */
async function adoptOrganization(org: Organization, from: string): Promise<void> {
  // Kept deliberately as a tripwire, not a diagnostic. After this fix
  // the only legitimate reason to reach here is a user with no tenant
  // claim at all, i.e. `from: "ABSENT"`. A hit carrying a `from` id
  // means the eviction survived and the diagnosis was wrong, so read
  // the two fields together rather than the message alone.
  console.error("[org-provider] AUTO-SWITCH firing", { to: org.id, from });

  try {
    // Update JWT claims to include this organization
    await fetch("/api/set-organization", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orgId: org.id }),
    });

    // Refresh session to get updated JWT
    await supabase.auth.refreshSession();
  } catch (error) {
    console.warn("Failed to set initial organization:", error);
  }
}

type OrganizationProviderProps = {
  children: ReactNode;
  /**
   * Built on the server by `loadOrganizationSnapshot` and handed over by the
   * root layout. When it is present the provider starts with the workspace
   * already known, and the mount fetch is skipped: the page no longer pays for
   * an `/api/organizations` request after paint. The fetch stays as the refresh
   * path, used after a switch and whenever the browser session names a
   * different user or tenant than the snapshot was built for.
   */
  initialSnapshot?: OrganizationSnapshot | null;
};

export const OrganizationProvider = ({ children, initialSnapshot }: OrganizationProviderProps) => {
  const { user, loading: userLoading } = useUser();
  const pathname = usePathname();

  const seed = useMemo(
    () => (initialSnapshot ? deriveOrganizationState(initialSnapshot, initialSnapshot.tenantId, { mayAdopt: false }) : null),
    [initialSnapshot]
  );

  const [currentOrg, setCurrentOrg] = useState<Organization | null>(seed?.currentOrg ?? null);
  const [homeOrganizations, setHomeOrganizations] = useState<Organization[]>(seed?.home ?? []);
  const [customerOrganizations, setCustomerOrganizations] = useState<Organization[]>(seed?.customers ?? []);
  const [customerParentMap, setCustomerParentMap] = useState<Record<string, string>>(seed?.customerParentMap ?? {});
  const [memberships, setMemberships] = useState<MembershipSummary[]>(seed?.memberships ?? []);
  const [loading, setLoading] = useState(seed === null);

  const isAuthPage = AUTH_PATHS.some((p) => pathname?.startsWith(p));

  // Sync org locale to NEXT_LOCALE cookie so server-side layouts pick it up
  useEffect(() => {
    if (currentOrg?.locale) {
      // biome-ignore lint/suspicious/noDocumentCookie: client-side locale cookie must be set via document.cookie for Next.js middleware
      document.cookie = `NEXT_LOCALE=${currentOrg.locale};path=/;max-age=31536000;SameSite=Lax`;
    }
  }, [currentOrg?.locale]);

  // Request sequencing. A switch fires this fetch explicitly AND re-fires the
  // effect on the new user identity, so several are in flight at once. Without
  // ordering, a response issued BEFORE the switch can land after it and write
  // the old org back over the new one. Only the newest request may write.
  const requestSeq = useRef(0);

  const applyDerived = (derived: DerivedOrganizationState) => {
    setHomeOrganizations(derived.home);
    setCustomerOrganizations(derived.customers);
    setCustomerParentMap(derived.customerParentMap);
    setMemberships(derived.memberships);
    setCurrentOrg(derived.currentOrg);

    if (derived.adopt) {
      return adoptOrganization(derived.adopt, derived.adoptFrom);
    }
    return Promise.resolve();
  };

  const fetchUserOrganizations = async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      const response = await fetch("/api/organizations");
      if (response.ok) {
        const data = (await response.json()) as OrganizationSnapshot;

        // Superseded by a newer request: drop it. Placed before every setter and
        // before the auto-switch POST below, so a stale response can write
        // neither React state nor the tenant claim.
        if (seq !== requestSeq.current) {
          return;
        }

        await applyDerived(deriveOrganizationState(data, user?.app_metadata?.tenant_id, { mayAdopt: true }));
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  // The snapshot is spent once the browser session moves past what it describes.
  // Keyed on user AND tenant, because a switch changes only the second.
  //
  // The mismatch is a normal state, not an error, and it is worth knowing what
  // it looks like. A switch made in ANOTHER window writes the tenant claim and
  // refreshes that window's token; this window keeps its old cookie token until
  // its own refresh. Server pages, filtered by that token, then describe the OLD
  // workspace, while the snapshot, built from the freshly read user record,
  // names the NEW one. The two disagree for exactly as long as the stale token
  // lives.
  //
  // Returning false here is the whole handling: it drops the seed and does the
  // fetch, which reads this browser's live claim and lands on whichever
  // workspace the session actually holds. Nothing tries to reconcile the two,
  // and above all nothing rewrites the claim to make them agree, because both
  // readings are of a real tenant and picking one would evict the person from
  // the other.
  const seedCoversSession = (): boolean => {
    if (!(seed && initialSnapshot && user)) {
      return false;
    }
    const tenantId = (user.app_metadata as Record<string, string | undefined> | undefined)?.tenant_id ?? null;
    return initialSnapshot.userId === user.id && initialSnapshot.tenantId === tenantId;
  };

  useEffect(() => {
    if (isAuthPage) {
      setLoading(false);
      return;
    }
    if (user && !userLoading) {
      // Seeded, for this exact user and tenant, and it names a workspace: the
      // fetch would return what is already on screen. A seed that names none
      // falls through instead of acting on itself, because filling an empty
      // claim is the refresh path's job and its alone.
      if (seedCoversSession() && seed?.currentOrg) {
        return;
      }
      fetchUserOrganizations();
    } else if (!seed) {
      setLoading(false);
    }
  }, [user, userLoading, isAuthPage]);

  const switchOrganization = async (orgId: string) => {
    try {
      const response = await fetch("/api/set-organization", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId }),
      });

      if (response.ok) {
        // Refresh session to get new JWT with updated app_metadata
        await supabase.auth.refreshSession();
        await fetchUserOrganizations();
      } else {
        throw new Error("Failed to switch organization");
      }
    } catch (error) {
      console.error("Failed to switch organization:", error);
      throw error;
    }
  };

  const organizations = useMemo(
    () =>
      [...homeOrganizations, ...customerOrganizations].filter((org, index, array) => org && array.findIndex((item) => item.id === org.id) === index),
    [homeOrganizations, customerOrganizations]
  );

  const firstAgency = useMemo(() => homeOrganizations.find((org) => org.organization_type === "agency"), [homeOrganizations]);

  // "Am I somewhere that isn't mine?" is the real question, and it is answered
  // by membership, not by org type. Keying on organization_type === "customer"
  // left platform staff with no way out of a partner AGENCY workspace, because
  // an agency is not a customer. Membership covers that case and the original
  // one with a single rule.
  const isForeignOrg = Boolean(currentOrg && !memberships.some((membership) => membership.orgId === currentOrg.id));

  const returnToAgency =
    (isForeignOrg || currentOrg?.organization_type === "customer") && firstAgency
      ? async () => {
          await switchOrganization(firstAgency.id);
        }
      : null;

  return (
    <OrganizationContext.Provider
      value={{
        currentOrg,
        organizations,
        homeOrganizations,
        customerOrganizations,
        customerParentMap,
        memberships,
        switchOrganization,
        returnToAgency,
        refreshOrganization: fetchUserOrganizations,
        loading: loading || userLoading,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};
